import { Box, Typography } from "@mui/material";
import { Link, useParams } from "@tanstack/react-router";

import { useNavGroups } from "./navConfig";
import type { NavGroup } from "./navConfig";
import { PrivilegedModeToggle } from "./PrivilegedModeToggle";
import { UserMenu } from "./UserMenu";
import type { ToOptions, LinkProps } from "@tanstack/react-router";

import CrownLogo from "@/components/CrownLogo/CrownLogo";
import { SiteSelectorDark } from "@/components/SiteSelectorDark";
import { useAuth } from "@/contexts/AuthContext";
import { appContentGutter } from "@/design-tokens/layout";
import { materialColorsOpacity } from "@/design-tokens/material-colors";
import { shape } from "@/design-tokens/shape";
import { typography } from "@/design-tokens/typography";
import { NotificationBell } from "@/features/Notifications/NotificationBell";
import { useCanAccessBesties } from "@/utils/privilegedMode";

interface TopNavProps {
  activeGroup: NavGroup | null;
}

export function TopNav({ activeGroup }: TopNavProps) {
  const { user } = useAuth();
  const routeParams = useParams({ strict: false });
  const siteId = routeParams?.siteId ?? "";

  const canAccessBestiesNow = useCanAccessBesties(
    user?.user?.role,
    user?.org_type,
  );
  const navGroups = useNavGroups();
  const visibleGroups = navGroups.filter((group) => {
    if (group.label === "Besties") {
      return canAccessBestiesNow;
    }
    return true;
  });

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 52,
        px: appContentGutter,
        backgroundColor: "background.paper-elevation-1",
      }}
    >
      {/* Left section: Logo + Site Selector */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        {siteId ? (
          <Link to="/dragon-tiles/$siteId" params={{ siteId }}>
            <CrownLogo width={38} height={38} />
          </Link>
        ) : (
          <Link to="/">
            <CrownLogo width={38} height={38} />
          </Link>
        )}
        <SiteSelectorDark />
      </Box>

      {/* Center section: Main nav group items */}
      <Box
        sx={{
          marginLeft: 11,
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexGrow: 1,
        }}
      >
        {visibleGroups.map((group) => {
          const isActive = activeGroup?.label === group.label;

          return (
            <Box
              key={group.label}
              component={Link}
              to={group.defaultRoute.to as ToOptions["to"]}
              params={{ siteId } as LinkProps["params"]}
              sx={{
                position: "relative",
                px: 5,
                py: 3,
                cursor: "pointer",
                textDecoration: "none",
                borderRadius: `${shape.borderRadius.md}px`,
                "&:hover": isActive
                  ? {}
                  : { backgroundColor: materialColorsOpacity.violet["400-4%"] },
                transition: "background-color 0.15s",
              }}
            >
              <Typography
                sx={{
                  fontSize: typography.fontSize["0.875rem"],
                  fontWeight: isActive
                    ? typography.fontWeight.semiBold
                    : typography.fontWeight.medium,
                  color: isActive ? "primary.main" : "text.primary",
                  whiteSpace: "nowrap",
                }}
              >
                {group.label}
              </Typography>
              {/* Active underline indicator */}
              {isActive && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -4,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "calc(100% - 2px)",
                    height: 2,
                    backgroundColor: "primary.main",
                    borderRadius: `${shape.borderRadius.none}px`,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Right section: User Menu */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <NotificationBell siteId={siteId} />
        <PrivilegedModeToggle />
        <UserMenu />
      </Box>
    </Box>
  );
}
